import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCaretDown, faCaretUp } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, TextField } from '@mui/material'
import Toolbar from '@mui/material/Toolbar'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ISO_DATE } from '@/api/constants'

dayjs.extend(advancedFormat)

type ReportsTableToolbarProps = {
  queryFilters: any
  setQueryFilters: Dispatch<SetStateAction<any>>
  label?: string
  clearable?: boolean
}

const DateFilterV2 = ({ queryFilters, setQueryFilters, clearable = true }: ReportsTableToolbarProps) => {
  const { t } = useTranslation()
  const [openStartDate, setOpenStartDate] = useState(false)
  const [openEndDate, setOpenEndDate] = useState(false)

  const handleDateChange = (key: string) => (date: Dayjs | null) => {
    setQueryFilters((prevState) => ({
      ...prevState,
      [key]: date?.format(ISO_DATE),
    }))

    setOpenStartDate(false)
    setOpenEndDate(false)
  }

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Box width={200} marginRight={1}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disableMaskedInput
            components={{
              OpenPickerIcon: () => (
                <FontAwesomeIcon icon={openStartDate ? faCaretUp : (faCaretDown as IconProp)} fontSize={16} />
              ),
            }}
            componentsProps={{
              actionBar: {
                actions: ['clear'],
              },
            }}
            maxDate={queryFilters.endDate ? dayjs(queryFilters.endDate) : undefined}
            maxDateMessage={t('datePicker.maxDateError')}
            inputFormat="LL"
            autoOk
            open={openStartDate}
            label={t('dateFilterComponent.from')}
            value={queryFilters.start_date ? dayjs(queryFilters.start_date) : null}
            onChange={handleDateChange('start_date')}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  mt: 2,
                  '& .MuiInputBase-input': {
                    padding: '14px 12px',
                  },
                  '& fieldset': { border: 0 },
                  '&:hover fieldset': { border: 0 },
                  '&& .Mui-focused fieldset': { border: 0 },
                }}
                inputProps={{ ...params.inputProps, readOnly: true }}
                onClick={(e) => setOpenStartDate(!openStartDate)}
              />
            )}
          />
        </LocalizationProvider>
      </Box>
      <Box width={200} marginRight={1}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disableMaskedInput
            components={{
              OpenPickerIcon: () => (
                <FontAwesomeIcon icon={openEndDate ? faCaretUp : (faCaretDown as IconProp)} fontSize={16} />
              ),
            }}
            componentsProps={{
              actionBar: {
                actions: ['clear'],
              },
            }}
            minDate={queryFilters.start_date ? dayjs(queryFilters.start_date) : undefined}
            minDateError={t('datePicker.minDateError')}
            inputFormat="LL"
            autoOk
            open={openEndDate}
            label={t('dateFilterComponent.to')}
            value={queryFilters.end_date ? dayjs(queryFilters.end_date) : null}
            onChange={handleDateChange('end_date')}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  mt: 2,
                  '& .MuiInputBase-input': {
                    padding: '14px 12px',
                  },
                  '& fieldset': { border: 0 },
                  '&:hover fieldset': { border: 0 },
                  '&& .Mui-focused fieldset': { border: 0 },
                }}
                inputProps={{ ...params.inputProps, readOnly: true }}
                onClick={(e) => setOpenEndDate(!openEndDate)}
              />
            )}
          />
        </LocalizationProvider>
      </Box>
    </Toolbar>
  )
}

export default DateFilterV2
